import { DatabaseViewInstitutionParamRequest } from '../../../api/api-types/database-view-request.api';
import { DatabaseViewSchema } from '../../../api/app-types/database-view.app';
import { DatabaseSortDirection } from '../shared/models/database-list.types';
import { validateSelectedColumns } from '../shared/utils/database-request-builder';

export type InstitutionListFilterKey = 'name' | 'schoolNumber' | 'category' | 'mainInstitution' | 'postalCodeCity';
export type InstitutionListSortField = InstitutionListFilterKey;

export interface InstitutionListQueryState {
	filterValues: Record<InstitutionListFilterKey, string[]>;
	filterSearch: Record<InstitutionListFilterKey, string>;
	sortField: InstitutionListSortField | null;
	sortDirection: DatabaseSortDirection;
}

const filterParamMap: Record<InstitutionListFilterKey, string> = {
	name: 'institutionBranch',
	schoolNumber: 'schoolNumber',
	category: 'category',
	mainInstitution: 'mainInstitution',
	postalCodeCity: 'address',
};

export const INSTITUTION_VISIBLE_COLUMNS = [
	'institutionBranch',
	'schoolNumber',
	'category',
	'mainInstitution',
	'address',
	'phoneNumber',
	'email',
	'advisorCount',
	'activeClientsCount',
];

export function buildDatabaseViewInstitutionRequest(
	state: InstitutionListQueryState,
	schema?: DatabaseViewSchema | null,
	selectedColumns: string[] = INSTITUTION_VISIBLE_COLUMNS,
): DatabaseViewInstitutionParamRequest {
	const payload: DatabaseViewInstitutionParamRequest = {
		selectedColumns: [...new Set(selectedColumns.filter((value) => value.trim().length > 0))],
	};
	validateSelectedColumns(schema, payload.selectedColumns);

	for (const key of Object.keys(filterParamMap) as InstitutionListFilterKey[]) {
		const selectedValues = state.filterValues[key] ?? [];
		const searchTerm = state.filterSearch[key]?.trim() ?? '';
		const values = [...selectedValues];
		if (searchTerm && !values.includes(searchTerm)) values.push(searchTerm);
		if (!values.length) continue;

		const fieldId = filterParamMap[key];
		const column = schema?.columns.find((candidate) => candidate.id === fieldId);
		const operator = selectedValues.length ? 'in' : 'contains';
		if (schema && (!column || !column.filterOperations.includes(operator))) {
			throw new Error(`Unsupported database view operation: ${fieldId}`);
		}
		(payload.filters ??= []).push({ fieldId, operator, values });
	}

	if (state.sortField) {
		const sortFieldId = filterParamMap[state.sortField];
		const sortColumn = schema?.columns.find((candidate) => candidate.id === sortFieldId);
		if (schema && (!sortColumn || !sortColumn.sortable))
			throw new Error(`Unsupported database view sort: ${sortFieldId}`);
		payload.sort = { fieldId: sortFieldId, ascending: state.sortDirection === 'asc' };
	}

	return payload;
}
